import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';
import get from 'lodash/get';

import {
  PIP_VALUE_CALCULATOR_FORM,
  MARGIN_CALCULATOR_FORM,
  SWAPS_CALCULATOR_FORM,
  PROFIT_LOSS_CALCULATOR_FORM,
} from '../constants';
import { formatCurrencySpecs, attachCurrency } from '../helpers';

const getCurrencyResults = (state) => get(state, 'tradingCalculators.results.currencyCalculator');
const getPipValueResults = (state) => get(state, 'tradingCalculators.results.pipValue');
const getMarginResults = (state) => get(state, 'tradingCalculators.results.margin');
const getSwapsResults = (state) => get(state, 'tradingCalculators.results.swaps');
const getProfitLossResults = (state) => get(state, 'tradingCalculators.results.profitLoss');
const currencySpecsSelector = (state) =>
  get(state, 'tradingCalculators.currencySpecs.specification');

export const pipValueFormSelector = formValueSelector(PIP_VALUE_CALCULATOR_FORM);
export const marginFormSelector = formValueSelector(MARGIN_CALCULATOR_FORM);
export const swapsFormSelector = formValueSelector(SWAPS_CALCULATOR_FORM);
export const profitLossFormSelector = formValueSelector(PROFIT_LOSS_CALCULATOR_FORM);

export const getSymbolConfig = (state) => {
  return state.tradingCalculators.currencySpecs.symbols;
};

export const getCurrentSymbolSelector = (form) => (state) => {
  const selector = formValueSelector(form);

  const symbol = selector(state, 'symbol');
  return symbol;
};

export const getAccountTypeSelector = (form) => (state) => {
  const selector = formValueSelector(form);

  const symbol = selector(state, 'accountType');
  return symbol;
};

export const getCurrencySpecs = createSelector(
  currencySpecsSelector,
  (currencySpecs) => currencySpecs,
);

export const getCurrencyPairs = createSelector(getCurrencySpecs, formatCurrencySpecs);

export const getCurrencyPairMarginStyle = createSelector(
  [(state) => marginFormSelector(state, 'currencyPair'), getCurrencySpecs],
  (currencyPair, currencySpecs) => get(currencySpecs, `${currencyPair}.marginStyle`, ''),
);

export const getPreparedCurrencyResults = createSelector(
  getCurrencyResults,
  ({ conversionRate, convertedAmount }) => [
    {
      conversionRate,
      convertedAmount,
    },
  ],
);

export const getPreparedPipValueResults = createSelector(
  getPipValueResults,
  ({ results, meta }) => {
    const { currentConversionPrice, accountPipValue, currencyPipValue } = results;
    const { baseCurrency, convertedCurrency } = meta;

    return [
      {
        currentConversionPrice,
        pipConvertedAmount: attachCurrency(accountPipValue, baseCurrency),
        pipConvertedCurrency: attachCurrency(currencyPipValue, convertedCurrency),
      },
    ];
  },
);

export const getPreparedMarginResults = createSelector(getMarginResults, ({ results, meta }) => {
  const { currentConversionPrice, accountRequiredMargin, symbolPrice } = results;
  const { baseCurrency, convertedCurrency } = meta;

  return [
    {
      price: attachCurrency(symbolPrice, convertedCurrency),
      currentConversionPrice,
    },
    {
      title: { key: 'tradingCalculators:results.accountBaseCurrency' },
      accountRequiredMargin: attachCurrency(accountRequiredMargin, baseCurrency),
    },
  ];
});

export const getPreparedSwapsResults = createSelector(getSwapsResults, ({ results, meta }) => {
  const {
    currentConversionPrice,
    swapLongCurrencyPair,
    swapShortCurrencyPair,
    swapLongCurrency,
    swapShortCurrency,
    swapLongConvertedCurrency,
    swapShortConvertedCurrency,
    price,
    type,
  } = results;
  const { baseCurrency, convertedCurrency } = meta;

  return [
    {
      currentConversionPrice,
      swapLongCurrencyPair,
      swapShortCurrencyPair,
      price,
      type,
    },
    {
      title: { key: 'tradingCalculators:results.accountBaseCurrency' },
      swapLongCurrency: attachCurrency(swapLongCurrency, baseCurrency),
      swapShortCurrency: attachCurrency(swapShortCurrency, baseCurrency),
    },
    {
      title: { key: 'tradingCalculators:results.convertedCurrency' },
      swapLongConvertedCurrency: attachCurrency(swapLongConvertedCurrency, convertedCurrency),
      swapShortConvertedCurrency: attachCurrency(swapShortConvertedCurrency, convertedCurrency),
    },
  ];
});

export const getPreparedProfitLossResults = createSelector(
  getProfitLossResults,
  ({ results, meta }) => {
    const { profitLossSymbol, profitLossBase, pipValue, conversionRate } = results;
    const { baseCurrency, symbolCurrency } = meta;

    return [
      {
        profitLossSymbol: attachCurrency(profitLossSymbol, symbolCurrency),
        profitLossBase: attachCurrency(profitLossBase, baseCurrency),
        conversionRate: attachCurrency(conversionRate, baseCurrency),
        pipValue,
      },
    ];
  },
);
